import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { WorkflowListing } from "../../../../api/content/workflowsApi";
import { stepDisplayStatesOf, type StepProgressState } from "../../../../api/threads/threadDecoders";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { appPaths } from "../../../../shared/routing/appPaths";
import AppButton from "../../../../ui/buttons/AppButton";
import Chip from "../../../../ui/feedback/Chip";
import { refusalText } from "../../../../ui/feedback/refusalWords";
import { stepKindOfDefinition } from "../../../workflows/graph/stepKind";
import { useThreadContext } from "../threadPageContext";
import type { StepRunner } from "../useStepRunner";
import { RunAgainMenu } from "../currentStep/ChangeStepMenu";
import CurrentStep from "../currentStep/CurrentStep";
import { actionOffer, allows, type ActionOffer } from "../viewerActions";
import type { StepFocusView } from "../currentStep/useStepFocus";
import { useRunDraft } from "../currentStep/useRunDraft";
import OutputsSoFar from "../switch/OutputsSoFar";
import { useSwitchOptions } from "../switch/useWorkflowSwitch";
import WorkflowSwitch from "../switch/WorkflowSwitch";
import { NO_REACH, runReach } from "./runReach";
import StepRow from "./StepRow";
import StopBeforeAgentSteps from "./StopBeforeAgentSteps";
import { outcomeView } from "./workflowOutcome";

interface WorkflowStepsProps {
  readonly focus: StepFocusView;
  readonly progress: Readonly<Record<string, StepProgressState>>;
  /** The step the host runs the Workflow to, from the server; `null` when no run to a step goes on. */
  readonly runTo: string | null;
  readonly runner: StepRunner;
  /** The content version's Workflows, which a switch offers (F126); `null` while they load. */
  readonly listing: WorkflowListing | null;
  /** The log's last entry, live: a switch appends on it. */
  readonly lastEntryNumber: number;
}

/**
 * The Thread's Workflow as its steps, like a notebook (Johannes, 28 Oct, F100): the current
 * step open, the steps before it folded into one line, the steps after it one line each; one
 * of those opens at a time, and closes when the current step moves on (TR-B1-04).
 * The Run's graph opens from the Run's header (F149). Each row names its state as module C shows it (W-22). With
 * "Stop before agent steps" on (O4), an agent step waits for Run. The host runs one step at a time, all of them ("Run all"), or up to a step
 * ("Run to here"); the server runs the steps up to it (`run_to`), and this shows how far it
 * runs, with Stop. At any step the host may switch to another Workflow fed by the outputs so
 * far, which show folded at the foot; the Workflows left fold into a line each (F126).
 */
const NO_SKIPS: Readonly<Record<string, readonly string[]>> = {};
const OUTCOME_TONES = { satisfied: "success", unsatisfied: "danger", undetermined: "neutral" } as const;

export default function WorkflowSteps({ focus, progress, runTo, runner, listing, lastEntryNumber }: WorkflowStepsProps) {
  const { t } = useTranslation();
  const { threadId, overview, openTurn, stepName, stepNameIn, workflowName, workflowNameOf } = useThreadContext();
  const [switching, setSwitching] = useState(false);
  const switchOptions = useSwitchOptions(threadId, `${overview.workflow ?? ""} ${JSON.stringify(progress)}`);
  const order = Object.keys(progress);
  const { steps, plans } = focus;
  // The typed values of the current step: Run, "Run to here" and "Run all" each send them (PC-3).
  const draft = useRunDraft(threadId, focus.current, overview.lastEntryNumber);
  const current = draft.current;
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [rowsOpenedAt, setRowsOpenedAt] = useState(current?.stepKey ?? null);
  if ((current?.stepKey ?? null) !== rowsOpenedAt) {
    setRowsOpenedAt(current?.stepKey ?? null);
    setOpenRow(null);
  }
  const numberOf = (stepKey: string) => steps.get(stepKey)?.number ?? String(order.indexOf(stepKey) + 1);
  const skips = overview.detail.run_skips ?? NO_SKIPS;
  const shown = stepDisplayStatesOf(overview.detail);
  const followed = overview.projection.workflow ?? null;
  const outcome = followed === null ? null : outcomeView(followed);
  const reach = useMemo(
    () =>
      draft.pending
        ? NO_REACH
        : runReach({
            progress,
            current,
            kindOf: (stepKey) => {
              const kind = steps.get(stepKey)?.step?.kind;
              return kind === undefined ? null : stepKindOfDefinition(kind);
            },
            startOf: (stepKey) => steps.get(stepKey)?.start ?? null,
            plans: current === null || draft.plan === null ? plans : new Map([...plans, [current.stepKey, draft.plan]]),
            skips,
          }),
    [progress, current, steps, plans, skips, draft.pending, draft.plan],
  );
  if (overview.workflow === null) return null;
  const submitting = runner.state.status === "submitting";
  // Module C's decisions for this viewer (W-22 slice 2): run, run to and stop; reach and readiness are the plans'. One
  // refused to this viewer stays in view and says why.
  const refusalWords = (offer: ActionOffer) => (offer.refusal === null ? undefined : refusalText(t, offer.refusal));
  const runToOffer = (stepKey: string) => actionOffer(overview.viewer, "run_to", stepKey);
  const runAll = runTo === null && reach.runAll !== null ? reach.runAll : null;
  const runAllOffer = runAll === null ? null : runToOffer(runAll);
  const stopOffer = runTo === null ? null : actionOffer(overview.viewer, "stop_run_to");
  const switchOffer = actionOffer(overview.viewer, "switch_workflow");
  const position = current === null ? order.length : current.position - 1;
  const before = order.slice(0, position);
  const after = current === null ? [] : order.slice(current.position);
  const mayRunThis = (stepKey: string) => allows(overview.viewer, "run_step", stepKey);
  // A step a run skipped runs again like a done one (PC-4).
  const earlier = order.filter((stepKey) => (progress[stepKey] === "done" || progress[stepKey] === "skipped") && mayRunThis(stepKey));
  /** What a step a row waits for is doing in this Run, as module C shows it (TR-B1-02, W-22). */
  const stateOf = (stepKey: string) => shown[stepKey] ?? null;
  const runToStep = (target: string) => {
    runner.act("run_to", target, draft.values);
  };
  /** What a run to `target` skips on the way, named in its button's title. */
  const skipsTitle = (target: string) => {
    const skipped = (skips[target] ?? []).filter((stepKey) => !(progress[stepKey] === "done" || progress[stepKey] === "skipped") && order.indexOf(stepKey) >= (current?.position ?? 1) - 1);
    return skipped.length === 0 ? undefined : t("threads.steps.skips", { steps: skipped.map((stepKey) => `${numberOf(stepKey)}. ${stepName(stepKey)}`).join(", ") });
  };
  /** A chip that opens the entry showing it, when one does (W-22). */
  const evidence = (entry: number | null | undefined, chip: ReactNode) =>
    entry == null ? chip : <Link to={appPaths.thread(threadId, entry)} title={`#${String(entry)}`} className="app-link app-link-cue-up">{chip}</Link>;
  const row = (stepKey: string, runToHere: ActionOffer | null) => (
    <StepRow
      key={stepKey}
      stepKey={stepKey}
      number={numberOf(stepKey)}
      step={steps.get(stepKey)}
      state={shown[stepKey]}
      plan={plans.get(stepKey)}
      plans={plans}
      bindings={focus.bindings}
      stepName={stepName}
      onRunTo={runToHere === null ? null : () => { runToStep(stepKey); }}
      runToUnavailable={runToHere === null ? undefined : refusalWords(runToHere)}
      runToTitle={skipsTitle(stepKey)}
      onRun={progress[stepKey] === "skipped" && mayRunThis(stepKey) ? (values) => { runner.run(stepKey, values); } : null}
      runBusy={submitting || openTurn !== null}
      busy={submitting}
      open={openRow === stepKey}
      onToggle={(open) => { setOpenRow(open ? stepKey : openRow === stepKey ? null : openRow); }}
      stateOf={stateOf}
    />
  );

  return (
    <section aria-label={t("threads.steps.label")} className="app-card space-y-2 p-3">
      <header className="flex flex-wrap items-center gap-2 text-xs">
        <span className="app-text font-semibold">{workflowName ?? t("threads.workflow.unnamed")}</span>
        {followed === null || (followed.active && !followed.completed)
          ? null
          : evidence(followed.completed ? followed.completed_entry : null, <Chip>{followed.completed ? t("threads.workflow.completed") : t("threads.workflow.left")}</Chip>)}
        {/* What the run did against what the Workflow promises, apart from its completion; a run of one that promises nothing shows its completion only. */}
        {outcome === null
          ? null
          : evidence(
              outcome.evidenceEntry,
              <Chip
                tone={OUTCOME_TONES[outcome.state]}
                title={outcome.conditions
                  .map((condition) => t("threads.workflow.condition", { step: stepName(condition.condition.step), state: t(`threads.workflow.conditionState.${condition.state}`) }))
                  .join("\n")}
              >
                {t(`threads.workflow.outcome.${outcome.state}`)}
              </Chip>,
            )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {/* "Stop before agent steps" (O4), for a Workflow with agent steps to stop before. */}
          {[...plans.values()].some((stepPlan) => stepPlan.stepKind === "agent") ? <StopBeforeAgentSteps /> : null}
          {runTo === null ? null : (
            <>
              <Chip tone="info" size="compact">{t("threads.steps.runningTo", { step: `${numberOf(runTo)}. ${stepName(runTo)}` })}</Chip>
              {stopOffer === null ? null : (
                <AppButton variant="ghost" size="xs" disabled={submitting} unavailable={refusalWords(stopOffer)} onClick={() => { runner.act("run_to", null); }}>
                  {t("threads.steps.stop")}
                </AppButton>
              )}
            </>
          )}
          {runAll === null || runAllOffer === null ? null : (
            <AppButton variant="secondary" size="xs" disabled={submitting} title={skipsTitle(runAll)} unavailable={refusalWords(runAllOffer)} onClick={() => { runToStep(runAll); }}>
              {t("threads.steps.runAll")}
            </AppButton>
          )}
          {/* With no step current, as when the Workflow is done, earlier steps can still run again. */}
          {current !== null || earlier.length === 0 ? null : (
            <RunAgainMenu earlier={earlier} stepName={stepName} busy={submitting || openTurn !== null} onRunAgainFrom={(stepKey) => { runner.run(stepKey); }} />
          )}
          {/* A switch is the host's entry in the log, as in a saved Thread too; it runs nothing. */}
          {switchOffer !== null && !switching ? (
            <AppButton variant="ghost" size="xs" unavailable={refusalWords(switchOffer)} onClick={() => { setSwitching(true); }}>{t("threads.switch.open")}</AppButton>
          ) : null}
        </span>
      </header>
      {/* Each Workflow left, as its switch recorded it (F126). */}
      {overview.departures.map((departure, index) => {
        const left = workflowNameOf(departure.workflow) ?? t("threads.switch.someWorkflow");
        const after = departure.afterStep === null ? null : stepNameIn(departure.workflow, departure.afterStep);
        return (
          <p key={`${String(index)}:${departure.workflow}`} className="app-text-muted text-xs">
            {after === null ? t("threads.switch.earlierUnsettled", { workflow: left }) : t("threads.switch.earlier", { workflow: left, step: after })}
          </p>
        );
      })}
      {switching ? (
        switchOptions.options === null ? (
          switchOptions.error === null ? <p className="app-text-muted text-xs">{t("threads.prepare.run.loadingWorkflows")}</p> : <ErrorNotice error={switchOptions.error} compact={true} />
        ) : (
          <WorkflowSwitch options={switchOptions.options} listing={listing} lastEntryNumber={lastEntryNumber} onClose={() => { setSwitching(false); }} />
        )
      ) : null}
      {before.length === 0 ? null : current === null ? (
        <ol className="space-y-0.5">{before.map((stepKey) => row(stepKey, null))}</ol>
      ) : (
        <details>
          <summary className="app-text-muted cursor-pointer text-xs">{t("threads.steps.before", { count: before.length })}</summary>
          <ol className="mt-1 space-y-0.5">{before.map((stepKey) => row(stepKey, null))}</ol>
        </details>
      )}
      <CurrentStep focus={focus} runner={runner} draft={draft} earlier={earlier} />
      {after.length === 0 ? null : (
        <ol className="space-y-0.5">
          {after.map((stepKey) => row(stepKey, runTo !== stepKey && reach.runTo.has(stepKey) ? runToOffer(stepKey) : null))}
        </ol>
      )}
      {current === null && runner.state.status === "failed" ? <ErrorNotice error={runner.state.error} compact={true} /> : null}
      <OutputsSoFar made={switchOptions.options?.made ?? []} />
    </section>
  );
}
